import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  ArrowDown,
  ArrowLeftRight,
  ArrowUp,
  Bold,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Code,
  Columns2,
  Columns3,
  FolderTree,
  GalleryHorizontal,
  Heading1,
  ImagePlus,
  Info,
  Link2,
  List,
  ListOrdered,
  Loader2,
  ChevronUp,
  Pencil,
  Plus,
  Save,
  Search,
  Shield,
  Table,
  Trash2,
  Upload,
  Video,
  Workflow,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../utils";
import type { UserRole } from "../types";
import type { WikiPage, WikiPageStatus, WikiVisibility } from "../models/wiki";
import { WIKI_STATUS_OPTIONS, WIKI_VISIBILITY_OPTIONS } from "../models/wiki";
import { ACCESS_ROLE_OPTIONS } from "../lib/accessControl";
import { buildWikiPageTree, slugifyWikiTitle } from "../lib/wiki";
import { WikiRenderer } from "./wiki/WikiRenderer";

type WikiApiPayload = {
  pages: WikiPage[];
  summaries: any[];
  tags: string[];
  categories: string[];
  availableDepartments: string[];
  recent: any[];
};

type WikiImportResponse = {
  importId: string;
  sourceName: string;
  summary: {
    markdownFiles: number;
    pagesPrepared: number;
    created: number;
    updated: number;
    skippedExisting: number;
    assetsCopied: number;
    internalLinksRewritten: number;
  };
  preview: Array<{
    title: string;
    slug: string;
    parentSlug: string | null;
    category: string;
  }>;
};

type EditorDraft = {
  id?: string;
  title: string;
  slug: string;
  parentId: string;
  order: number;
  content: string;
  contentFormat: "markdown" | "mdx" | "structured";
  tags: string;
  category: string;
  visibility: WikiVisibility;
  allowedRoles: UserRole[];
  allowedDepartments: string[];
  status: WikiPageStatus;
};

const NEW_PAGE_TEMPLATE = `# New page

Start writing here.
`;

function buildHeaders(user: { uid: string; email?: string | null } | null) {
  return {
    "Content-Type": "application/json",
    "x-user-uid": user?.uid || "",
    "x-user-email": user?.email || "",
  };
}

function toEditorDraft(page?: WikiPage | null, suggestedParentId = ""): EditorDraft {
  if (!page) {
    return {
      title: "",
      slug: "",
      parentId: suggestedParentId,
      order: 0,
      content: NEW_PAGE_TEMPLATE,
      contentFormat: "markdown",
      tags: "",
      category: "",
      visibility: "all_staff",
      allowedRoles: [],
      allowedDepartments: [],
      status: "draft",
    };
  }

  return {
    id: page.id,
    title: page.title,
    slug: page.slug,
    parentId: page.parentId || "",
    order: page.order || 0,
    content: page.content || "",
    contentFormat: page.contentFormat,
    tags: page.tags.join(", "),
    category: page.category || "",
    visibility: page.visibility,
    allowedRoles: page.allowedRoles || [],
    allowedDepartments: page.allowedDepartments || [],
    status: page.status,
  };
}

function buildBreadcrumbs(page: WikiPage | null, pageMap: Map<string, WikiPage>) {
  if (!page) return [];
  const trail: WikiPage[] = [];
  let current: WikiPage | undefined | null = page;
  while (current) {
    trail.unshift(current);
    current = current.parentId ? pageMap.get(current.parentId) || null : null;
  }
  return trail;
}

function collectDescendantIds(pageId: string, pages: WikiPage[]) {
  const descendants = new Set<string>();
  const childrenByParent = new Map<string, WikiPage[]>();
  pages.forEach((page) => {
    const parentKey = page.parentId || "";
    childrenByParent.set(parentKey, [...(childrenByParent.get(parentKey) || []), page]);
  });
  const walk = (id: string) => {
    const children = childrenByParent.get(id) || [];
    children.forEach((child) => {
      descendants.add(child.id);
      walk(child.id);
    });
  };
  walk(pageId);
  return descendants;
}

function WikiTree({
  nodes,
  currentPageId,
  onSelect,
  collapsedIds,
  onToggleCollapsed,
}: {
  nodes: ReturnType<typeof buildWikiPageTree>;
  currentPageId: string | null;
  onSelect: (pageId: string) => void;
  collapsedIds: Set<string>;
  onToggleCollapsed: (pageId: string) => void;
}) {
  return (
    <div className="space-y-1">
      {nodes.map((node) => {
        const isCollapsed = collapsedIds.has(node.id);
        const hasChildren = node.children.length > 0;
        return (
          <div key={node.id}>
            <button
              type="button"
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                currentPageId === node.id
                  ? "bg-[#fff4cc] text-slate-900"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              )}
              onClick={() => onSelect(node.id)}
            >
              {hasChildren ? (
                <span
                  className="flex h-4 w-4 items-center justify-center text-slate-400"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleCollapsed(node.id);
                  }}
                >
                  {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </span>
              ) : (
                <span className="h-4 w-4" />
              )}
              <span className="truncate">{node.title}</span>
            </button>
            {hasChildren && !isCollapsed ? (
              <div className="ml-4 border-l border-slate-200 pl-2 dark:border-slate-800">
                <WikiTree
                  nodes={node.children}
                  currentPageId={currentPageId}
                  onSelect={onSelect}
                  collapsedIds={collapsedIds}
                  onToggleCollapsed={onToggleCollapsed}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function Wiki() {
  const { user, profile, hasPermission } = useAuth();
  const [payload, setPayload] = useState<WikiApiPayload | null>(null);
  const [people, setPeople] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditorDraft>(() => toEditorDraft(null));
  const [previewMode, setPreviewMode] = useState<"edit" | "preview" | "split">("edit");
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [sidebarMode, setSidebarMode] = useState<"tree" | "recent" | "tags">("tree");
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [importZipFile, setImportZipFile] = useState<File | null>(null);
  const [importOverwrite, setImportOverwrite] = useState(false);
  const [importingPreview, setImportingPreview] = useState(false);
  const [applyingImport, setApplyingImport] = useState(false);
  const [importResult, setImportResult] = useState<WikiImportResponse | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const canEditWiki = hasPermission("wiki.edit");
  const canManageWiki = hasPermission("wiki.manage");

  const buildAuthHeaders = () => ({
    "x-user-uid": user?.uid || "",
    "x-user-email": user?.email || "",
  });

  const fetchWiki = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [wikiRes, peopleRes] = await Promise.all([
        fetch("/api/wiki/pages", { headers: buildHeaders(user) }),
        fetch("/api/people", { headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" } }),
      ]);

      if (!wikiRes.ok) {
        const data = await wikiRes.json().catch(() => ({}));
        throw new Error(data.error || "Failed to load wiki");
      }

      const nextPayload = (await wikiRes.json()) as WikiApiPayload;
      setPayload(nextPayload);
      if (peopleRes.ok) {
        setPeople(await peopleRes.json());
      }
      setSelectedPageId((current) => {
        if (current && nextPayload.pages.some((page) => page.id === current)) return current;
        return nextPayload.pages[0]?.id || null;
      });
    } catch (error: any) {
      console.error("Failed to load wiki", error);
      toast.error(error.message || "Failed to load wiki");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchWiki();
  }, [user]);

  const pages = payload?.pages || [];
  const pageMap = useMemo(() => new Map(pages.map((page) => [page.id, page])), [pages]);
  const slugMap = useMemo(
    () =>
      Object.fromEntries(
        pages.map((page) => [page.title, page.slug])
      ),
    [pages]
  );

  const filteredPages = useMemo(() => {
    return pages.filter((page) => {
      if (selectedCategory && page.category !== selectedCategory) return false;
      if (selectedTag && !page.tags.includes(selectedTag)) return false;
      if (!searchQuery.trim()) return true;
      const haystack = [page.title, page.slug, page.category, page.tags.join(" "), page.content]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchQuery.trim().toLowerCase());
    });
  }, [pages, searchQuery, selectedCategory, selectedTag]);

  const filteredPageIds = new Set(filteredPages.map((page) => page.id));
  const tree = useMemo(
    () => buildWikiPageTree(filteredPages.map((page) => ({ ...page }))),
    [filteredPages]
  );
  const selectedPage = selectedPageId ? pageMap.get(selectedPageId) || null : null;
  const breadcrumbs = useMemo(() => buildBreadcrumbs(selectedPage, pageMap), [selectedPage, pageMap]);
  const recentPages = payload?.recent || [];
  const availableDepartments = payload?.availableDepartments || [];

  const beginCreatePage = (parentId = "") => {
    setEditing(true);
    setShowAdvancedSettings(false);
    setPreviewMode("edit");
    setDraft(toEditorDraft(null, parentId));
  };

  const beginEditPage = (page: WikiPage) => {
    setEditing(true);
    setShowAdvancedSettings(false);
    setDraft(toEditorDraft(page));
  };

  const handleInsertSnippet = (snippet: string) => {
    setDraft((current) => ({ ...current, content: `${current.content.trimEnd()}\n\n${snippet}\n` }));
    textareaRef.current?.focus();
  };

  const handleUploadAsset = async (accept = "image/*", type: "image" | "video" = "image") => {
    if (!user) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      setUploadingAsset(true);
      try {
        const res = await fetch("/api/upload", {
          method: "POST",
          headers: {
            "x-user-uid": user.uid,
            "x-user-email": user.email || "",
          },
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || "Failed to upload asset");
        }
        const snippet =
          type === "video"
            ? `::video{src="${data.url}" title="${file.name}"}`
            : `::image{src="${data.url}" alt="${file.name}" align="center" width="md" caption=""}`;
        handleInsertSnippet(snippet);
        toast.success(`${type === "video" ? "Video" : "Image"} uploaded`);
      } catch (error: any) {
        toast.error(error.message || "Failed to upload asset");
      } finally {
        setUploadingAsset(false);
      }
    };
    input.click();
  };

  const resetImportPanel = () => {
    setImportZipFile(null);
    setImportOverwrite(false);
    setImportResult(null);
    setImportingPreview(false);
    setApplyingImport(false);
  };

  const handlePreviewImport = async () => {
    if (!user) return;
    if (!importZipFile) {
      toast.error("Choose a ZIP export first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", importZipFile);
    setImportingPreview(true);
    try {
      const res = await fetch("/api/wiki/import/preview", {
        method: "POST",
        headers: buildAuthHeaders(),
        body: formData,
      });
      const data = (await res.json().catch(() => ({}))) as any;
      if (!res.ok) {
        throw new Error(data.error || "Failed to preview wiki import");
      }
      setImportResult(data as WikiImportResponse);
      toast.success("Wiki import preview ready");
    } catch (error: any) {
      toast.error(error.message || "Failed to preview wiki import");
    } finally {
      setImportingPreview(false);
    }
  };

  const handleApplyImport = async () => {
    if (!user || !importResult) return;
    setApplyingImport(true);
    try {
      const res = await fetch("/api/wiki/import/apply", {
        method: "POST",
        headers: buildHeaders(user),
        body: JSON.stringify({
          importId: importResult.importId,
          overwrite: importOverwrite,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as any;
      if (!res.ok) {
        throw new Error(data.error || "Failed to import wiki ZIP");
      }
      toast.success("Wiki imported");
      setShowImportPanel(false);
      resetImportPanel();
      setEditing(false);
      await fetchWiki();
    } catch (error: any) {
      toast.error(error.message || "Failed to import wiki ZIP");
    } finally {
      setApplyingImport(false);
    }
  };

  const handleSave = async (statusOverride?: WikiPageStatus) => {
    if (!user) return;
    if (!draft.title.trim()) {
      toast.error("Page title is required.");
      return;
    }

    setSaving(true);
    try {
      const payloadBody = {
        title: draft.title.trim(),
        slug: slugifyWikiTitle(draft.slug || draft.title),
        parentId: draft.parentId || null,
        order: draft.order || 0,
        content: draft.content,
        contentFormat: draft.contentFormat,
        tags: draft.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        category: draft.category.trim(),
        visibility: draft.visibility,
        allowedRoles: draft.allowedRoles,
        allowedDepartments: draft.allowedDepartments,
        status: statusOverride || draft.status,
      };
      const res = await fetch(draft.id ? `/api/wiki/pages/${draft.id}` : "/api/wiki/pages", {
        method: draft.id ? "PUT" : "POST",
        headers: buildHeaders(user),
        body: JSON.stringify(payloadBody),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to save wiki page");
      }
      toast.success(statusOverride === "published" ? "Wiki page published" : "Wiki page saved");
      setEditing(false);
      await fetchWiki();
      setSelectedPageId(data.id);
    } catch (error: any) {
      toast.error(error.message || "Failed to save wiki page");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    await handleSave("archived");
  };

  const handleMovePage = async (page: WikiPage, direction: "up" | "down") => {
    if (!user) return;
    const siblingPages = pages
      .filter((candidate) => (candidate.parentId || "") === (page.parentId || ""))
      .sort((a, b) => a.order - b.order);
    const currentIndex = siblingPages.findIndex((candidate) => candidate.id === page.id);
    const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex === -1 || swapIndex < 0 || swapIndex >= siblingPages.length) return;

    const current = siblingPages[currentIndex];
    const target = siblingPages[swapIndex];
    try {
      const updates = [
        { id: current.id, parentId: current.parentId || null, order: target.order },
        { id: target.id, parentId: target.parentId || null, order: current.order },
      ];
      const res = await fetch("/api/wiki/pages/reorder", {
        method: "POST",
        headers: buildHeaders(user),
        body: JSON.stringify({ updates }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to reorder wiki pages");
      }
      await fetchWiki();
    } catch (error: any) {
      toast.error(error.message || "Failed to reorder wiki pages");
    }
  };

  const pageChoicesForParent = useMemo(() => {
    const blockedIds = draft.id ? collectDescendantIds(draft.id, pages) : new Set<string>();
    return pages.filter((page) => page.id !== draft.id && !blockedIds.has(page.id));
  }, [draft.id, pages]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[480px] items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading wiki…
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-[calc(100vh-10rem)] gap-6 md:grid-cols-[300px,minmax(0,1fr)]">
      <aside className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-4 py-4 dark:border-slate-800">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400">Wiki</p>
              <h2 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">Internal knowledge</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {canManageWiki ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowImportPanel((current) => !current);
                    if (showImportPanel) {
                      resetImportPanel();
                    }
                  }}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition",
                    showImportPanel
                      ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  )}
                >
                  <Upload className="h-4 w-4" />
                  Import ZIP
                </button>
              ) : null}
              {canEditWiki ? (
                <button
                  type="button"
                  onClick={() => beginCreatePage()}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 transition hover:brightness-95"
                >
                  <Plus className="h-4 w-4" />
                  New page
                </button>
              ) : null}
            </div>
          </div>
          <div className="relative mt-4">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search pages, tags, or content"
              className="app-control w-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none ring-0 transition focus:border-[#ffcc01] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
          </div>
          <div className="mt-3 flex gap-2">
            {[
              { key: "tree", label: "Tree", icon: FolderTree },
              { key: "recent", label: "Recent", icon: BookOpen },
              { key: "tags", label: "Tags", icon: Workflow },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setSidebarMode(item.key as typeof sidebarMode)}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition",
                    sidebarMode === item.key
                      ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900"
                      : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-800 dark:border-slate-700 dark:text-slate-300"
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="max-h-[calc(100vh-14rem)] overflow-y-auto px-3 py-3">
          {sidebarMode === "tree" ? (
            <WikiTree
              nodes={tree}
              currentPageId={selectedPageId}
              onSelect={setSelectedPageId}
              collapsedIds={collapsedIds}
              onToggleCollapsed={(pageId) => {
                setCollapsedIds((current) => {
                  const next = new Set(current);
                  if (next.has(pageId)) next.delete(pageId);
                  else next.add(pageId);
                  return next;
                });
              }}
            />
          ) : sidebarMode === "recent" ? (
            <div className="space-y-1">
              {recentPages.map((page: any) => (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => setSelectedPageId(page.id)}
                  className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <p className="font-medium">{page.title}</p>
                  <p className="mt-1 text-xs text-slate-400">{new Date(page.updatedAt).toLocaleDateString("en-GB")}</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Categories</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedCategory("")}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium",
                      !selectedCategory ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900" : "border-slate-200 text-slate-600"
                    )}
                  >
                    All
                  </button>
                  {(payload?.categories || []).map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setSelectedCategory(category)}
                      className={cn(
                        "rounded-full border px-2.5 py-1 text-xs font-medium",
                        selectedCategory === category ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900" : "border-slate-200 text-slate-600"
                      )}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Tags</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedTag("")}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium",
                      !selectedTag ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900" : "border-slate-200 text-slate-600"
                    )}
                  >
                    All
                  </button>
                  {(payload?.tags || []).map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setSelectedTag(tag)}
                      className={cn(
                        "rounded-full border px-2.5 py-1 text-xs font-medium",
                        selectedTag === tag ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900" : "border-slate-200 text-slate-600"
                      )}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {showImportPanel && canManageWiki ? (
          <div className="border-b border-slate-100 p-5 dark:border-slate-800">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Wiki import</p>
                <h3 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">Import Notion ZIP</h3>
                <p className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                  Upload your exported ZIP, preview the page hierarchy and assets, then apply the import when you are happy with it.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowImportPanel(false);
                  resetImportPanel();
                }}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr),260px]">
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">ZIP export</label>
                <input
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(event) => {
                    setImportZipFile(event.target.files?.[0] || null);
                    setImportResult(null);
                  }}
                  className="app-control w-full border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none"
                />
                <p className="mt-2 text-xs text-slate-500">
                  Choose the original Notion export ZIP. The importer will unpack it server-side and preserve the page tree where possible.
                </p>
                <label className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={importOverwrite}
                    onChange={(event) => setImportOverwrite(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-[#ffcc01] focus:ring-[#ffcc01]"
                  />
                  Overwrite existing pages with matching slugs when applying
                </label>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Actions</p>
                <div className="mt-3 space-y-2">
                  <button
                    type="button"
                    disabled={!importZipFile || importingPreview}
                    onClick={handlePreviewImport}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    {importingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Preview import
                  </button>
                  <button
                    type="button"
                    disabled={!importResult || applyingImport}
                    onClick={handleApplyImport}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 transition hover:brightness-95 disabled:opacity-50"
                  >
                    {applyingImport ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                    Apply import
                  </button>
                </div>
              </div>
            </div>

            {importResult ? (
              <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Preview</p>
                    <h4 className="mt-1 text-lg font-semibold text-slate-900">{importResult.sourceName}</h4>
                  </div>
                  <div className="text-sm text-slate-500">
                    {importResult.summary.pagesPrepared} pages prepared • {importResult.summary.assetsCopied} assets found
                  </div>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  {[
                    { label: "Create", value: importResult.summary.created },
                    { label: "Update", value: importOverwrite ? importResult.summary.updated : 0 },
                    { label: "Skip", value: importOverwrite ? importResult.summary.skippedExisting : importResult.summary.skippedExisting + importResult.summary.updated },
                    { label: "Links rewritten", value: importResult.summary.internalLinksRewritten },
                  ].map((item) => (
                    <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{item.label}</p>
                      <p className="mt-2 text-2xl font-semibold text-slate-900">{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Title</th>
                        <th className="px-4 py-3">Slug</th>
                        <th className="px-4 py-3">Parent</th>
                        <th className="px-4 py-3">Category</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {importResult.preview.map((item) => (
                        <tr key={item.slug}>
                          <td className="px-4 py-2.5 font-medium text-slate-900">{item.title}</td>
                          <td className="px-4 py-2.5 text-slate-500">{item.slug}</td>
                          <td className="px-4 py-2.5 text-slate-500">{item.parentSlug || "Top level"}</td>
                          <td className="px-4 py-2.5 text-slate-500">{item.category || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {editing ? (
          <div className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4 dark:border-slate-800">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Editor</p>
                <h3 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
                  {draft.id ? "Edit wiki page" : "Create wiki page"}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                {draft.id && canManageWiki ? (
                  <button
                    type="button"
                    onClick={handleArchive}
                    className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-50"
                  >
                    <Archive className="h-4 w-4" />
                    Archive
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => handleSave("draft")}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save draft
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => handleSave("published")}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 transition hover:brightness-95 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                  Publish
                </button>
              </div>
            </div>

            <div className="mt-5 space-y-5">
              <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr),320px]">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Title</label>
                  <input
                    value={draft.title}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        title: event.target.value,
                        slug: current.id ? current.slug : slugifyWikiTitle(event.target.value),
                      }))
                    }
                    className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Parent</label>
                  <select
                    value={draft.parentId}
                    onChange={(event) => setDraft((current) => ({ ...current, parentId: event.target.value }))}
                    className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                  >
                    <option value="">Top level</option>
                    {pageChoicesForParent.map((page) => (
                      <option key={page.id} value={page.id}>
                        {page.title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedSettings((current) => !current)}
                    className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-800"
                  >
                    {showAdvancedSettings ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {showAdvancedSettings ? "Hide advanced settings" : "Show advanced settings"}
                  </button>
                </div>
              </div>

              {showAdvancedSettings ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Slug</label>
                      <input
                        value={draft.slug}
                        onChange={(event) => setDraft((current) => ({ ...current, slug: slugifyWikiTitle(event.target.value) }))}
                        className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Order</label>
                      <input
                        type="number"
                        value={draft.order}
                        onChange={(event) => setDraft((current) => ({ ...current, order: Number(event.target.value) || 0 }))}
                        className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Status</label>
                      <select
                        value={draft.status}
                        onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value as WikiPageStatus }))}
                        className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                      >
                        {WIKI_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Visibility</label>
                      <select
                        value={draft.visibility}
                        onChange={(event) => setDraft((current) => ({ ...current, visibility: event.target.value as WikiVisibility }))}
                        className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                      >
                        {WIKI_VISIBILITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Category</label>
                      <input
                        value={draft.category}
                        onChange={(event) => setDraft((current) => ({ ...current, category: event.target.value }))}
                        className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Tags</label>
                      <input
                        value={draft.tags}
                        onChange={(event) => setDraft((current) => ({ ...current, tags: event.target.value }))}
                        placeholder="workflow, onboarding, software"
                        className="app-control w-full border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                      />
                    </div>
                  </div>

                  {draft.visibility === "roles" || canManageWiki ? (
                    <div className="mt-4">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Allowed roles</label>
                      <div className="flex flex-wrap gap-2">
                        {ACCESS_ROLE_OPTIONS.map((option) => {
                          const active = draft.allowedRoles.includes(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                setDraft((current) => ({
                                  ...current,
                                  allowedRoles: active
                                    ? current.allowedRoles.filter((role) => role !== option.value)
                                    : [...current.allowedRoles, option.value],
                                }))
                              }
                              className={cn(
                                "rounded-full border px-2.5 py-1 text-xs font-medium transition",
                                active ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900" : "border-slate-200 text-slate-600"
                              )}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {draft.visibility === "departments" || canManageWiki ? (
                    <div className="mt-4">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Allowed departments</label>
                      <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                        {availableDepartments.map((department) => {
                          const active = draft.allowedDepartments.includes(department);
                          return (
                            <button
                              key={department}
                              type="button"
                              onClick={() =>
                                setDraft((current) => ({
                                  ...current,
                                  allowedDepartments: active
                                    ? current.allowedDepartments.filter((item) => item !== department)
                                    : [...current.allowedDepartments, department],
                                }))
                              }
                              className={cn(
                                "rounded-full border px-2.5 py-1 text-xs font-medium transition",
                                active ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900" : "border-slate-200 text-slate-600"
                              )}
                            >
                              {department}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="min-w-0">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: "H1", icon: Heading1, snippet: "# Heading" },
                      { label: "Bold", icon: Bold, snippet: "**Bold text**" },
                      { label: "Bullets", icon: List, snippet: "- First item\n- Second item" },
                      { label: "Numbers", icon: ListOrdered, snippet: "1. First item\n2. Second item" },
                      { label: "Link", icon: Link2, snippet: "[Link text](https://example.com)" },
                      { label: "Table", icon: Table, snippet: "| Column | Column |\n| --- | --- |\n| Value | Value |" },
                      { label: "Callout", icon: Info, snippet: "::callout{type=\"info\" title=\"Important\"}\nHelpful note here.\n::" },
                      { label: "2 Col", icon: Columns2, snippet: "::columns\n:::column\nLeft content\n:::\n:::column\nRight content\n:::\n::" },
                      { label: "3 Col", icon: Columns3, snippet: "::columns\n:::column\nColumn one\n:::\n:::column\nColumn two\n:::\n:::column\nColumn three\n:::\n::" },
                      { label: "Gallery", icon: GalleryHorizontal, snippet: "::gallery\n/uploads/image-one.jpg | Caption one\n/uploads/image-two.jpg | Caption two\n::" },
                      { label: "People", icon: Workflow, snippet: "::peopleGrid{department=\"Comp\"}" },
                      { label: "Code", icon: Code, snippet: "```ts\nconsole.log(\"Wiki\")\n```" },
                    ].map((tool) => {
                      const Icon = tool.icon;
                      return (
                        <button
                          key={tool.label}
                          type="button"
                          onClick={() => handleInsertSnippet(tool.snippet)}
                          className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {tool.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      disabled={uploadingAsset}
                      onClick={() => handleUploadAsset("image/*", "image")}
                      className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      {uploadingAsset ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                      Image
                    </button>
                    <button
                      type="button"
                      disabled={uploadingAsset}
                      onClick={() => handleUploadAsset("video/*", "video")}
                      className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      <Video className="h-3.5 w-3.5" />
                      Video
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {(["edit", "split", "preview"] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setPreviewMode(mode)}
                        className={cn(
                          "rounded-md border px-2.5 py-1.5 text-xs font-semibold capitalize",
                          previewMode === mode ? "border-[#ffcc01] bg-[#fff4cc] text-slate-900" : "border-slate-200 text-slate-500"
                        )}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={cn("grid gap-4", previewMode === "split" ? "xl:grid-cols-2" : "grid-cols-1")}>
                  {previewMode !== "preview" ? (
                    <textarea
                      ref={textareaRef}
                      value={draft.content}
                      onChange={(event) => setDraft((current) => ({ ...current, content: event.target.value }))}
                      className="min-h-[720px] w-full rounded-lg border border-slate-200 bg-white px-4 py-3 font-mono text-sm text-slate-800 outline-none focus:border-[#ffcc01]"
                    />
                  ) : null}
                  {previewMode !== "edit" ? (
                    <div className="min-h-[720px] rounded-lg border border-slate-200 bg-slate-50/60 px-6 py-5">
                      <WikiRenderer
                        content={draft.content}
                        people={people}
                        pageSlugMap={slugMap}
                        onNavigateToPage={(slug) => {
                          const target = pages.find((page) => page.slug === slug);
                          if (target) {
                            setSelectedPageId(target.id);
                            setEditing(false);
                          }
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : selectedPage ? (
          <div className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-4 dark:border-slate-800">
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.id}>
                      {index > 0 ? <ChevronRight className="h-4 w-4 text-slate-300" /> : null}
                      <button type="button" onClick={() => setSelectedPageId(crumb.id)} className="hover:text-slate-800">
                        {crumb.title}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
                <h1 className="text-4xl font-semibold text-slate-900 dark:text-slate-100">{selectedPage.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  {selectedPage.category ? <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium">{selectedPage.category}</span> : null}
                  {selectedPage.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-500">
                      {tag}
                    </span>
                  ))}
                  <span>Updated {new Date(selectedPage.updatedAt).toLocaleDateString("en-GB")}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {canEditWiki ? (
                  <>
                    <button
                      type="button"
                      onClick={() => beginCreatePage(selectedPage.id)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      <Plus className="h-4 w-4" />
                      Child page
                    </button>
                    <button
                      type="button"
                      onClick={() => beginEditPage(selectedPage)}
                      className="inline-flex items-center gap-2 rounded-lg border border-[#ffcc01] bg-[#fff4cc] px-3 py-2 text-sm font-semibold text-slate-900 transition hover:brightness-95"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit page
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMovePage(selectedPage, "up")}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      <ArrowUp className="h-4 w-4" />
                      Up
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMovePage(selectedPage, "down")}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      <ArrowDown className="h-4 w-4" />
                      Down
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            <div className="mt-6 max-w-5xl">
              <WikiRenderer
                content={selectedPage.content}
                people={people}
                pageSlugMap={slugMap}
                onNavigateToPage={(slug) => {
                  const target = pages.find((page) => page.slug === slug);
                  if (target) setSelectedPageId(target.id);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex min-h-[480px] items-center justify-center p-8 text-center">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">No pages yet</h3>
              <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
                Create the first internal page, or import your Notion Markdown export into the new wiki.
              </p>
              {canEditWiki ? (
                <button
                  type="button"
                  onClick={() => beginCreatePage()}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 transition hover:brightness-95"
                >
                  <Plus className="h-4 w-4" />
                  Create first page
                </button>
              ) : null}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
